import * as React from "react";
import styled from "styled-components";
import { SortableTree } from "dnd-kit-sortable-tree";
import { Task as TaskInner } from "./Task";
import { AnyTaggedItem, TreeOf } from "src/types";
import { Tree } from "src/utils/Tree";
import { ItemChangedReason } from "dnd-kit-sortable-tree/dist/types";

export type TaskTreeEditorOnMoveItem = (
  item: AnyTaggedItem,
  parent: AnyTaggedItem | null,
  index: number,
) => void | Promise<void>;

interface Props {
  collapse?: boolean;
  items: TreeOf<AnyTaggedItem>[];
  onMoveItem: TaskTreeEditorOnMoveItem;
}

type Collapsible = AnyTaggedItem & {
  collapsed: boolean;
  children: Collapsible[];
};

function collapse(
  nodes: TreeOf<AnyTaggedItem>[],
  expanded: Set<string>,
  collapseByDefault: boolean,
): Collapsible[] {
  return nodes.map((node): Collapsible => {
    return {
      ...node,
      collapsed: collapseByDefault === !expanded.has(node.id),
      children: collapse(node.children, expanded, collapseByDefault),
    };
  });
}

export function TaskTreeEditor({ items, collapse: shouldCollapse = false, onMoveItem }: Props) {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const collapsedTasks = React.useMemo(
    () => collapse(items, expanded, shouldCollapse),
    [items, expanded],
  );

  const handleChange = (items: Collapsible[], reason: ItemChangedReason<AnyTaggedItem>) => {
    switch (reason.type) {
      case "collapsed":
      case "expanded": {
        const newExpanded = new Set<string>(expanded);
        if (expanded.has(reason.item.id)) {
          newExpanded.delete(reason.item.id);
        } else {
          newExpanded.add(reason.item.id);
        }
        setExpanded(newExpanded);
        return;
      }
      case "dropped": {
        console.log("dropped", reason);
        const parentId = reason.droppedToParent?.id ?? null;
        const childSet = parentId ? Tree.find(items, (i) => i.id === parentId)?.children : items;
        if (!childSet) return;
        const idx = childSet.findIndex((i) => i.id === reason.draggedItem.id);
        console.log(`dropped to parent ${parentId} at index ${idx}`);
        void onMoveItem(reason.draggedItem, reason.droppedToParent, idx);
        return;
      }
      default:
        return;
    }
  };

  return (
    <Container>
      <Tasks>
        <SortableTree
          items={collapsedTasks}
          onItemsChanged={handleChange}
          TreeItemComponent={TaskInner}
          sortableProps={{ animateLayoutChanges: () => false }}
          dropAnimation={null}
          indentationWidth={25}
        />
      </Tasks>
    </Container>
  );
}

const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Tasks = styled.div`
  flex-grow: 1;
`;
